import React from 'react';
import {
  BarChart, Bar, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer,
  AreaChart, Area, CartesianGrid, LabelList,
} from 'recharts';

// Distinct, premium palette so each compartment bar reads as its own color.
export const CHART_COLORS = [
  '#2563eb', '#f97316', '#10b981', '#8b5cf6', '#ec4899',
  '#14b8a6', '#f59e0b', '#6366f1', '#ef4444', '#06b6d4',
];

const hrs = (ms) => +(((ms ?? 0) / 3_600_000).toFixed(2));
const fmtHrs = (h) => (h >= 1 ? `${h.toFixed(1)}h` : `${Math.round(h * 60)}m`);

function fmtDate(d) {
  if (!d) return '';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return String(d);
  return dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function ChartCard({ title, theme, children, empty }) {
  const dark = theme === 'dark';
  return (
    <div className={`rounded-xl border p-4 ${dark ? 'border-gray-700 bg-gray-800/40' : 'border-gray-200 bg-white'} shadow-sm`}>
      <h3 className={`text-sm font-semibold mb-3 ${dark ? 'text-gray-200' : 'text-gray-700'}`}>{title}</h3>
      {empty
        ? <div className="h-56 flex items-center justify-center text-sm text-gray-400">No usage recorded for this period.</div>
        : <div className="h-56">{children}</div>}
    </div>
  );
}

function Tip({ active, payload, label, theme, suffix }) {
  if (!active || !payload?.length) return null;
  const dark = theme === 'dark';
  return (
    <div className={`rounded-lg px-3 py-2 text-xs shadow-lg border ${dark ? 'bg-gray-900 border-gray-700 text-gray-100' : 'bg-white border-gray-200 text-gray-800'}`}>
      <div className="font-semibold mb-0.5">{label}</div>
      <div style={{ color: payload[0].color || payload[0].fill }}>{fmtHrs(payload[0].value)}{suffix}</div>
    </div>
  );
}

export default function UsageNodeCharts({ compartments = [], trend = [], theme }) {
  const dark = theme === 'dark';
  const axis = dark ? '#9ca3af' : '#6b7280';
  const grid = dark ? '#374151' : '#e5e7eb';
  const accent = CHART_COLORS[0];

  const bar = compartments.slice(0, 10).map(c => ({ name: c.compartment, hours: hrs(c.total_duration_ms) }));
  const line = trend.map(t => ({ date: fmtDate(t.date), hours: hrs(t.total_duration_ms) }));

  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <ChartCard title="Time by compartment" theme={theme} empty={!bar.length}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={bar} layout="vertical" margin={{ left: 8, right: 36, top: 4, bottom: 4 }} barCategoryGap="22%">
            <CartesianGrid horizontal={false} stroke={grid} strokeDasharray="3 3" />
            <XAxis type="number" hide />
            <YAxis
              type="category"
              dataKey="name"
              width={120}
              tick={{ fill: axis, fontSize: 12 }}
              tickLine={false}
              axisLine={false}
            />
            <Tooltip content={<Tip theme={theme} />} cursor={{ fill: dark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)' }} />
            <Bar dataKey="hours" radius={[0, 6, 6, 0]} maxBarSize={22}>
              {bar.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              <LabelList dataKey="hours" position="right" formatter={fmtHrs} style={{ fill: axis, fontSize: 11 }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="Usage trend" theme={theme} empty={!line.length}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={line} margin={{ left: 4, right: 12, top: 8, bottom: 4 }}>
            <defs>
              <linearGradient id="usageTrendFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={accent} stopOpacity={0.35} />
                <stop offset="100%" stopColor={accent} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid vertical={false} stroke={grid} strokeDasharray="3 3" />
            <XAxis dataKey="date" tick={{ fill: axis, fontSize: 11 }} tickLine={false} axisLine={{ stroke: grid }} minTickGap={24} />
            <YAxis tick={{ fill: axis, fontSize: 11 }} tickLine={false} axisLine={false} width={32} tickFormatter={(v) => `${v}h`} />
            <Tooltip content={<Tip theme={theme} />} cursor={{ stroke: accent, strokeWidth: 1, strokeDasharray: '4 4' }} />
            <Area
              type="monotone"
              dataKey="hours"
              stroke={accent}
              strokeWidth={2.5}
              fill="url(#usageTrendFill)"
              dot={false}
              activeDot={{ r: 4, fill: accent, stroke: dark ? '#0b1220' : '#fff', strokeWidth: 2 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </ChartCard>
    </div>
  );
}
